import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { overlay } from 'overlay-kit';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import z from 'zod';
import { api, getErrorMessage } from '@/shared/api/api';
import { Button, Input, Label } from '@/shared/ui';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Text } from '@/shared/ui/text';

const passwordSchema = z
  .object({
    oldPassword: z.string().min(1, { message: '이전 비밀번호를 입력하세요.' }),
    password: z
      .string()
      .min(8, { message: '비밀번호는 최소 8자 이상이어야 합니다.' })
      .regex(/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/'`~;])/, {
        message: '영문, 숫자, 특수문자를 모두 포함해야 합니다.',
      }),
    confirmPassword: z.string().min(1, { message: '비밀번호 확인을 입력하세요.' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: '비밀번호가 일치하지 않습니다.',
    path: ['confirmPassword'],
  });

type PasswordFormData = z.infer<typeof passwordSchema>;

export function PersonalSettingsTab() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    oldPassword: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof PasswordFormData, string>>>({});

  const { mutate, isPending } = useMutation({
    mutationKey: ['update-password'],
    mutationFn: async ({ oldPassword, password }: { oldPassword: string; password: string }) => {
      await api.post('users/change-password', {
        old_password: oldPassword,
        new_password: password,
      });
    },
    onSuccess: async () => {
      toast.success('비밀번호가 변경되었습니다. 다시 로그인해주세요.');

      const result = await overlay.openAsync(({ isOpen, close }) => (
        <Dialog open={isOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>비밀번호 변경 안내</DialogTitle>
              <DialogDescription>비밀번호가 변경되었습니다. 다시 로그인해주세요.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button
                  onClick={() => close(true)}
                  variant="primary"
                >
                  확인
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));

      if (result) {
        navigate('/signin');
      }
    },
    onError: async (error) => {
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validation = passwordSchema.safeParse(formData);

    if (!validation.success) {
      const fieldErrors: Partial<Record<keyof PasswordFormData, string>> = {};
      for (const error of validation.error.errors) {
        const path = error.path[0] as keyof PasswordFormData;
        fieldErrors[path] = error.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    mutate({
      oldPassword: formData.oldPassword,
      password: formData.password,
    });
  };

  const handleChange =
    (field: keyof PasswordFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      // 입력 시 해당 필드의 에러 제거
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  return (
    <div className="flex flex-col gap-2 shrink-0">
      <Text
        tag="h1"
        typography="typo-title-m-bold"
      >
        개인 설정
      </Text>

      <form
        className="flex flex-col gap-4 mt-6 w-full max-w-[620px]"
        onSubmit={handleSubmit}
      >
        <fieldset className="flex flex-col gap-4">
          <legend className="sr-only">비밀번호 변경</legend>

          <div className="flex flex-col gap-2">
            <Label htmlFor="oldPassword">
              <Text typography="typo-text-xl">이전 비밀번호</Text>
            </Label>
            <Input
              disabled={isPending}
              id="oldPassword"
              name="oldPassword"
              onChange={handleChange('oldPassword')}
              placeholder="이전 비밀번호를 입력해주세요."
              type="password"
              value={formData.oldPassword}
            />
            {errors.oldPassword && (
              <Text
                className="text-red-500"
                typography="typo-text-s"
              >
                {errors.oldPassword}
              </Text>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="password">
              <Text typography="typo-text-xl">변경할 비밀번호</Text>
            </Label>
            <Input
              disabled={isPending}
              id="password"
              name="password"
              onChange={handleChange('password')}
              placeholder="새 비밀번호를 입력해주세요. (최소 8자, 영문/숫자/특수문자 포함)"
              type="password"
              value={formData.password}
            />
            {errors.password && (
              <Text
                className="text-red-500"
                typography="typo-text-s"
              >
                {errors.password}
              </Text>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="confirmPassword">
              <Text typography="typo-text-xl">비밀번호 확인</Text>
            </Label>
            <Input
              disabled={isPending}
              id="confirmPassword"
              name="confirmPassword"
              onChange={handleChange('confirmPassword')}
              placeholder="비밀번호를 다시 입력해주세요."
              type="password"
              value={formData.confirmPassword}
            />
            {errors.confirmPassword && (
              <Text
                className="text-red-500"
                typography="typo-text-s"
              >
                {errors.confirmPassword}
              </Text>
            )}
          </div>
        </fieldset>

        <div className="flex justify-end">
          <Button
            disabled={isPending}
            pending={isPending}
            size="lg"
            type="submit"
            variant="primary"
          >
            비밀번호 변경
          </Button>
        </div>
      </form>
    </div>
  );
}
